import React from 'react';
import { Dialog, DialogTitle, DialogContent, List, ListItem, IconButton, TextField, ListItemText, ListItemSecondaryAction, DialogActions, Button } from '@mui/material';
import { CheckCircle as CheckCircleIcon, RadioButtonUnchecked as RadioButtonUncheckedIcon, Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { useTranslation } from '../../../context/TranslationContext';

interface ManageDatasetsDialogProps {
  open: boolean;
  onClose: () => void;
  datasets: { id: string; name: string }[];
  activeDatasetId: string;
  handleSetActiveDataset: (id: string) => void;
  editingDatasetId: string | null;
  editedDatasetName: string;
  handleEditDatasetName: (id: string, name: string) => void;
  handleSaveDatasetName: (id: string) => void;
  handleCancelEditDatasetName: () => void;
  handleRemoveDataset: (id: string) => void;
  handleOpenAddDatasetDialog: () => void;
  trimAndSetEditedDatasetName: (name: string) => void;
  handleKeyPress: (e: React.KeyboardEvent, onSave: () => void, onCancel: () => void) => void;
}

const ManageDatasetsDialog: React.FC<ManageDatasetsDialogProps> = ({
  open,
  onClose,
  datasets,
  activeDatasetId,
  handleSetActiveDataset,
  editingDatasetId,
  editedDatasetName,
  handleEditDatasetName,
  handleSaveDatasetName,
  handleCancelEditDatasetName,
  handleRemoveDataset,
  handleOpenAddDatasetDialog,
  trimAndSetEditedDatasetName,
  handleKeyPress
}) => {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ style: { minWidth: '700px', maxHeight: "80%", overflowY: 'auto' } }}>
      <DialogTitle>
        {t("Manage Datasets")}
        <Button onClick={handleOpenAddDatasetDialog} style={{ float: 'right' }}>
          <AddIcon /> {t("Add Dataset")}
        </Button>
      </DialogTitle>
      <DialogContent>
        <List>
          {datasets?.map(dataset => (
            <ListItem key={dataset.id}>
              <IconButton onClick={() => handleSetActiveDataset(dataset.id)}>
                {activeDatasetId === dataset.id ? <CheckCircleIcon color="primary" /> : <RadioButtonUncheckedIcon />}
              </IconButton>
              {editingDatasetId === dataset.id ? (
                <TextField
                  value={editedDatasetName}
                  onChange={(e) => trimAndSetEditedDatasetName(e.target.value)}
                  style={{ marginRight: '8px', width: 480 }}
                  onKeyUp={(e) => handleKeyPress(e, () => handleSaveDatasetName(dataset.id), handleCancelEditDatasetName)}
                  onKeyDown={(e) => e.key === 'Escape' && e.stopPropagation()}
                />
              ) : (
                <ListItemText primary={dataset.name} />
              )}
              <ListItemSecondaryAction>
                {editingDatasetId === dataset.id ? (
                  <>
                    <IconButton onClick={() => handleSaveDatasetName(dataset.id)}>
                      <SaveIcon />
                    </IconButton>
                    <IconButton onClick={handleCancelEditDatasetName}>
                      <CancelIcon />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <IconButton onClick={() => handleEditDatasetName(dataset.id, dataset.name)}>
                      <EditIcon />
                    </IconButton>
                    {datasets.length > 1 && (
                      <IconButton onClick={() => handleRemoveDataset(dataset.id)}>
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </>
                )}
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          {t("Close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManageDatasetsDialog;
